import { useMachine } from '@xstate/react';
import React from 'react';

import { Skew } from 'components/Button/FilledButton';
import CloseButton from 'images/close-button';
import {
  createQuizMachine,
  QuizMachineContext,
} from 'components/EducationQuiz/EducationQuizMachine';
import { useTranslations } from 'settings/i18n';
import useLinkCreator from 'hooks/useLinkCreator';
import { useIsRtl } from 'hooks/useCurrentLocale';
import useWindowSize from 'hooks/useWindowSize';

import {
  Answers,
  AnswerButton,
  AreYouSureCancellationText,
  CancelQuizButton,
  CancelQuizDialog,
  ContinueQuizButton,
  CurrentQuestionText,
  MobileQuestionTiles,
  MobileQuizLine,
  NextQuestionButton,
  QuestionTile,
  QuestionTiles,
  QuizLine,
  QuizNumberOfQuestions,
  QuizQuestion,
  QuizStartButton,
  QuizStats,
  QuizText,
  QuizWrapper,
  CancelQuizModalCloseIcon,
  QuizCancellationButtonsWrapper,
} from './styled';

const EducationQuiz = ({
  questions,
}: {
  questions: QuizMachineContext['questions'];
}) => {
  const { width } = useWindowSize();
  const isRtl = useIsRtl();
  const createLink = useLinkCreator();
  const [current, send] = useMachine(createQuizMachine(questions, createLink));

  const {
    currentQuestionIndex,
    questions: quizQuestions,
    answers,
  } = current.context;

  const currentQuestionId = questions[currentQuestionIndex].id;
  const isNextQuestionButtonDisabled = answers[currentQuestionId] === null;

  const t = useTranslations();

  const quizCancellationButtonsChangeBreakpoint = 550;

  return (
    <main style={{ minHeight: '100vh' }}>
      {current.matches('starting') && (
        <QuizWrapper>
          <QuizStats>
            <QuizText isRtl={isRtl}>{t('quiz')}</QuizText>
            <QuizLine />
            <CloseButton onClick={() => send('CANCEL')} />
          </QuizStats>
          <MobileQuizLine />

          <QuizQuestion isRtl={isRtl}>{t('quizStartText')}</QuizQuestion>

          <QuizNumberOfQuestions>{t('questions')}</QuizNumberOfQuestions>

          <QuizStartButton
            isRtl={isRtl}
            skew={Skew['top-right']}
            onClick={() => send('START_QUIZ')}>
            {t('start')}
          </QuizStartButton>
        </QuizWrapper>
      )}

      {current.matches('question') && (
        <QuizWrapper>
          <QuizStats>
            <QuizText isRtl={isRtl}>{t('quiz')}</QuizText>

            <CurrentQuestionText>
              {currentQuestionIndex + 1}/{quizQuestions.length}
              <QuestionTiles>
                {quizQuestions.map((question, index) => (
                  <QuestionTile
                    key={question.id}
                    active={currentQuestionIndex >= index}
                    numberOfQuestions={quizQuestions.length}
                  />
                ))}
              </QuestionTiles>
            </CurrentQuestionText>

            <CloseButton onClick={() => send('CANCEL')} />
          </QuizStats>

          <MobileQuestionTiles>
            {quizQuestions.map((question, index) => (
              <QuestionTile
                key={question.id}
                active={currentQuestionIndex >= index}
                numberOfQuestions={quizQuestions.length}
              />
            ))}
          </MobileQuestionTiles>

          <QuizQuestion isRtl={isRtl}>
            {t(quizQuestions[currentQuestionIndex].question)}
          </QuizQuestion>

          <Answers>
            {quizQuestions[currentQuestionIndex].answers.map(
              (answer, index) => (
                <li key={answer}>
                  <AnswerButton
                    selected={
                      answers[quizQuestions[currentQuestionIndex].id] === index
                    }
                    onClick={() =>
                      send('ANSWER', {
                        answerIndex:
                          answers[quizQuestions[currentQuestionIndex].id] ===
                          index
                            ? null
                            : index,
                      })
                    }>
                    {t(answer)}
                  </AnswerButton>
                </li>
              ),
            )}
          </Answers>

          <NextQuestionButton
            isRtl={isRtl}
            disabled={isNextQuestionButtonDisabled}
            skew={Skew['top-right']}
            onClick={() => send('NEXT_QUESTION')}>
            {t('next')}
          </NextQuestionButton>
        </QuizWrapper>
      )}

      {(current.matches('starting.cancelling') ||
        current.matches('question.cancelling')) && (
        <CancelQuizDialog
          onDismiss={() => send('CONTINUE')}
          aria-label={t('cancelTheQuiz')}>
          <CancelQuizModalCloseIcon
            color="#C7C7C7"
            onClick={() => send('CONTINUE')}
          />
          <AreYouSureCancellationText
            isRtl={isRtl}
            as="h2"
            size="pageTitleMobile"
            color="dark"
            weight="medium"
            letterSpacing="tight">
            {t('areYouSureYouWantToCancelTheQuiz')}
          </AreYouSureCancellationText>
          <QuizCancellationButtonsWrapper isRtl={isRtl}>
            {width > quizCancellationButtonsChangeBreakpoint && (
              <>
                <CancelQuizButton
                  isRtl={isRtl}
                  onClick={() => send('CANCEL')}
                  skew={Skew['top-right']}>
                  {t('cancelTheQuiz')}
                </CancelQuizButton>
                <ContinueQuizButton
                  isRtl={isRtl}
                  onClick={() => send('CONTINUE')}
                  skew={Skew['top-right']}>
                  {t('continueTheQuiz')}
                </ContinueQuizButton>
              </>
            )}

            {width <= quizCancellationButtonsChangeBreakpoint && (
              <>
                <ContinueQuizButton
                  isRtl={isRtl}
                  onClick={() => send('CONTINUE')}
                  skew={Skew['bottom-right']}>
                  {t('continueTheQuiz')}
                </ContinueQuizButton>
                <CancelQuizButton
                  isRtl={isRtl}
                  onClick={() => send('CANCEL')}
                  skew={Skew['top-right']}>
                  {t('cancelTheQuiz')}
                </CancelQuizButton>
              </>
            )}
          </QuizCancellationButtonsWrapper>
        </CancelQuizDialog>
      )}
    </main>
  );
};

export default EducationQuiz;
